import React, { memo, useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { reduxForm } from "redux-form";
import { createStructuredSelector } from "reselect";
import {
  clearData,
  createInitialOrder,
  fetchPaymentRequest,
  postPaymentRequest,
  fetchReferralCode,
  fetchUpfrontPaymentDetails,
  processUpfrontPayment,
  changeFormField
} from "ducks/actions";
import {
  makeSelectIsFetchingData,
  makeSelectOrder,
  makeSelectOrderParams,
  makeSelectPayMonthlyItems,
  makeSelectPayTodayItems,
  makeSelectDeliveryDetailsItems,
  makeSelectReferralCode,
  makeSelectPaymentRequest,
  makeSelectOrderSummaryForm,
  makeSelectOrderSummaryFormInitialValues,
  makeSelectFormattedSelectedNumber,
  makeSelectAddons,
  makeSelectDefaultPlanAddons,
  makeSelectPlanDetails,
  makeSelectDefaultPromoCodeOffers,
  makeSelectSessionToken,
  makeSelectPlanName,
  makeSelectPaymentDetails,
  makeSelectAppSettings,
  makeSelectMyInfoIsUsed,
  makeSelectIsWalletPromoCode,
  makeSelectValidateOrderError
} from "ducks/selectors";
import SmartRender from "containers/SmartRender";
import { forms } from "utils/constants";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";

const TempOrderSummaryHOC = Component =>
  function Comp(props) {
    const {
      order,
      orderParams,
      clearOrder,
      createInitialOrder: callCreateInitialOrder,
      clearPaymentRequest,
      clearPaymentDetails,
      referralCode,
      formValues,
      myInfoIsUsed,
      isWalletPromoCode,
      appSettings,
      changeField
    } = props;

    const [lastPromoCode, setLastPromoCode] = useState(
      get(referralCode, "value")
    );

    useEffect(
      () => {
        clearOrder();
        clearPaymentRequest();
        clearPaymentDetails();
        callCreateInitialOrder(orderParams);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [callCreateInitialOrder]
    );

    useEffect(
      () => {
        if (!isEmpty(order.error)) {
          clearOrder();
          // history.push("/sim_activation/sim"); // TODO: URL should not be hardcoded
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [order.error]
    );

    useEffect(
      () => {
        const promoCode = get(referralCode, "value");
        if (promoCode !== lastPromoCode) {
          setLastPromoCode(promoCode);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [referralCode, lastPromoCode, setLastPromoCode]
    );

    useEffect(
      () => {
        const noUpfrontPayment = get(props, "payTodayItems.totalAmount") < 1;
        const showWallet =
          noUpfrontPayment && myInfoIsUsed && isWalletPromoCode;
        const defaultWalletPayment =
          showWallet && get(appSettings, "enable_wallet_payment_default");
        const defaultPaymentType = defaultWalletPayment
          ? "wallet"
          : "recurring";
        if (formValues?.payment_type !== defaultPaymentType) {
          changeField("payment_type", defaultPaymentType);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    return <Component {...props} />;
  };

const mapStateToProps = createStructuredSelector({
  formValues: makeSelectOrderSummaryForm(),
  order: makeSelectOrder(),
  paymentRequest: makeSelectPaymentRequest(),
  orderParams: makeSelectOrderParams("sim_activation"),
  isCreatingOrder: makeSelectIsFetchingData("order"),
  isFetchingPaymentRequest: makeSelectIsFetchingData("paymentRequest"),
  payMonthlyItems: makeSelectPayMonthlyItems(),
  payTodayItems: makeSelectPayTodayItems("OrderSummary"),
  deliveryDetailsItems: makeSelectDeliveryDetailsItems(),
  referralCode: makeSelectReferralCode(),
  initialValues: makeSelectOrderSummaryFormInitialValues(),
  selectedNumber: makeSelectFormattedSelectedNumber(),
  addons: makeSelectAddons(),
  defaultPlanAddons: makeSelectDefaultPlanAddons(),
  planDetails: makeSelectPlanDetails(),
  defaultPromoCodeOffers: makeSelectDefaultPromoCodeOffers(),
  token: makeSelectSessionToken(),
  planName: makeSelectPlanName(),
  isFetchingPaymentDetails: makeSelectIsFetchingData("paymentDetails"),
  paymentDetails: makeSelectPaymentDetails(),
  appSettings: makeSelectAppSettings(),
  myInfoIsUsed: makeSelectMyInfoIsUsed(),
  isWalletPromoCode: makeSelectIsWalletPromoCode(),
  validateOrderError: makeSelectValidateOrderError()
});

export function mapDispatchToProps(dispatch) {
  return {
    clearOrder: () => {
      dispatch(clearData("order"));
    },
    clearPaymentRequest: () => {
      dispatch(clearData("paymentRequest"));
    },
    createInitialOrder: (orderParams, callback) => {
      if (!isEmpty(orderParams)) {
        dispatch(createInitialOrder(orderParams, callback));
      }
    },
    fetchPaymentRequest: params => {
      dispatch(fetchPaymentRequest(params));
    },
    postPaymentRequest: (params, callback) => {
      dispatch(postPaymentRequest(params, callback));
    },
    validateReferralCode: (referral_code, plan_name) =>
      dispatch(fetchReferralCode({ referral_code, plan_name })),
    resetReferralCode: () =>
      dispatch(clearData(["referralCode", "selectedAddons"])),
    fetchUpfrontPaymentDetails: (params, callback) => {
      dispatch(fetchUpfrontPaymentDetails(params, callback));
    },
    processUpfrontPayment: (params, callback) => {
      dispatch(processUpfrontPayment(params, callback));
    },
    clearPaymentDetails: () => {
      dispatch(clearData("paymentDetails"));
    },
    changeField: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.ORDER_SUMMARY_FORM,
          field: field,
          value: value
        })
      );
    }
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.ORDER_SUMMARY_FORM,
    destroyOnUnmount: false
  }),
  memo
)(TempOrderSummaryHOC(SmartRender));
